import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { cssVarColors } from '../Constants';

export const SpanWrapper = styled('span')``;

export const LinkWrapper = styled(Link)`
	text-decoration: none;
	color: ${cssVarColors.primary};
	cursor: pointer;
	:hover {
		color: ${cssVarColors.highlight};
	}
`;

type ConvertTextToLinksProps = {
	text: string;
};

type Frame = {
	name: 'href' | 'i' | 'b';
	props: Record<string, any>;
	children: React.ReactNode[];
	keyBase: string;
};

const createEl = (name: Frame['name'], props: Record<string, any>, children: React.ReactNode[], key: string) => {
	switch (name) {
		case 'href':
			return React.createElement(LinkWrapper, { key, to: props.to }, children);
		case 'i':
			return React.createElement('i', { key }, children);
		case 'b':
			return React.createElement('b', { key }, children);
	}
};

export const convertTextToLinks: React.FC<ConvertTextToLinksProps> = ({ text }): ReactElement => {
	// Matches: <href='...'>, </href>, <i>, </i>, <b>, </b>
	const tagRe = /<(href='([^']*)'|\/href|i|\/i|b|\/b)>/g;

	let lastIndex = 0;
	let match: RegExpExecArray | null;
	let keySeq = 0;

	const rootChildren: React.ReactNode[] = [];
	const stack: Frame[] = [];

	const pushText = (raw: string) => {
		if (raw.length === 0) return;
		// Preserve your SpanWrapper usage for text nodes
		const node = React.createElement(SpanWrapper, { key: `t-${keySeq++}` }, raw);
		const target = stack.length ? stack[stack.length - 1].children : rootChildren;
		target.push(node);
	};

	const pushNode = (node: React.ReactNode) => {
		const target = stack.length ? stack[stack.length - 1].children : rootChildren;
		target.push(node);
	};

	while ((match = tagRe.exec(text)) !== null) {
		// Text before this tag
		if (match.index > lastIndex) {
			pushText(text.slice(lastIndex, match.index));
		}

		const token = match[1]; // e.g., "href='...'", "/href", "i", "/i", "b", "/b"
		if (token.startsWith("href='")) {
			// Open <href='...'>
			const href = match[2];
			stack.push({
				name: 'href',
				props: { to: href },
				children: [],
				keyBase: `href-${match.index}`,
			});
		} else if (token === '/href') {
			// Close </href>
			if (stack.length && stack[stack.length - 1].name === 'href') {
				const frame = stack.pop()!;
				const node = createEl(frame.name, frame.props, frame.children, `${frame.keyBase}-${keySeq++}`);
				pushNode(node);
			} else {
				// Unmatched close: treat literally
				pushText(match[0]);
			}
		} else if (token === 'i') {
			stack.push({ name: 'i', props: {}, children: [], keyBase: `i-${match.index}` });
		} else if (token === '/i') {
			if (stack.length && stack[stack.length - 1].name === 'i') {
				const frame = stack.pop()!;
				const node = createEl(frame.name, frame.props, frame.children, `${frame.keyBase}-${keySeq++}`);
				pushNode(node);
			} else {
				pushText(match[0]);
			}
		} else if (token === 'b') {
			stack.push({ name: 'b', props: {}, children: [], keyBase: `b-${match.index}` });
		} else if (token === '/b') {
			if (stack.length && stack[stack.length - 1].name === 'b') {
				const frame = stack.pop()!;
				const node = createEl(frame.name, frame.props, frame.children, `${frame.keyBase}-${keySeq++}`);
				pushNode(node);
			} else {
				pushText(match[0]);
			}
		}

		lastIndex = tagRe.lastIndex;
	}

	// Trailing text
	if (lastIndex < text.length) {
		pushText(text.slice(lastIndex));
	}

	// If there are unclosed tags, render them literally (best-effort)
	while (stack.length) {
		const frame = stack.pop()!;
		// Reconstruct literal open tag + children + (optional) close tag
		const open = frame.name === 'href' ? `<href='${frame.props.to}'>` : `<${frame.name}>`;
		pushText(open);
		frame.children.forEach((child) => pushNode(child));
		const close = `</${frame.name}>`;
		pushText(close);
	}

	return React.createElement(React.Fragment, null, rootChildren);
};

// Example:
// convertTextToLinks({
// 	text: "This story is told <i>in two parts</i>, <href='./useCase?id=gCodeEfficacy'><b>click here</b></href> for part one."
// });
